import { ComponentEnum, DateTimeTypeEnum, ImageTypeEnum } from "../../../types";
import { DEFAULT_END_TIME, DEFAULT_START_TIME } from "./addToCalendarDefaults";

/**
 * Transfers variables from source content to destination content based on COMPONENT_GROUPS configuration
 * @param sourceContent - The original component content
 * @param destinationContent - The new component content template
 * @param sourceComponentType - The original component type
 * @param destinationComponentType - The new component type
 * @param componentGroups - The component groups configuration
 * @returns The destination content with transferred variables
 */
export const transferContentVariables = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sourceContent: Record<string, any>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  destinationContent: Record<string, any>,
  sourceComponentType: ComponentEnum,
  destinationComponentType: ComponentEnum,
  componentGroups: Array<{
    title: string;
    components: ComponentEnum[];
    variableGroups: string[][];
  }>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Record<string, any> => {
  // If source and destination are the same, return destination content as is
  if (sourceComponentType === destinationComponentType) {
    return destinationContent;
  }

  // Find the component group (same for both source and destination)
  const componentGroup = componentGroups.find(
    (group) =>
      group.components.includes(sourceComponentType) &&
      group.components.includes(destinationComponentType),
  );

  // If component types are not in the same group, return destination content as is
  if (!componentGroup) {
    return destinationContent;
  }

  // Transfer variables from source to destination based on variable groups
  const transferredContent = { ...destinationContent };

  componentGroup.variableGroups.forEach((variableGroup) => {
    // Check if destination has any variable from this group
    const destinationVariables = variableGroup.filter((variable) =>
      Object.prototype.hasOwnProperty.call(destinationContent, variable),
    );
    if (destinationVariables.length > 0) {
      // Find the first non-empty variable from this group in source content
      const sourceVariable = variableGroup.find(
        (variable) =>
          sourceContent[variable] !== undefined &&
          sourceContent[variable] !== "",
      );
      if (sourceVariable) {
        // Transfer to the first available destination variable
        const destinationVariable = destinationVariables[0];
        transferredContent[destinationVariable] = sourceContent[sourceVariable];
      }
    }
  });

  return transferredContent;
};

export const getNewComponentContent = (componentType: ComponentEnum) => {
  switch (componentType) {
    case ComponentEnum.TEXT:
      return { text: "" };

    case ComponentEnum.IMAGE:
      return { imageId: "", width: 100 };

    case ComponentEnum.VIDEO:
      return { title: "", url: "", transcript: "" };

    case ComponentEnum.MULTIPLE_CHOICE:
      return {
        label: "",
        choice_1: "",
        choice_2: "",
        choice_3: "",
        other: false,
        msq: false,
        variable: "",
      };

    case ComponentEnum.RATING_SCALE:
      return {
        label: "",
        minimumValue: "",
        maximumValue: "",
        numberOfSteps: "",
        minimumLabel: "",
        centerLabel: "",
        maximumLabel: "",
        variable: "",
      };

    case ComponentEnum.LESSON_LINK:
      return {
        lesson_id: "",
        module_id: "",
        page_id: "",
        label: "",
      };
    case ComponentEnum.INPUT:
      return { label: "", placeholder: "", variable: "" };

    case ComponentEnum.LOGIC_COMPONENT:
      return {};

    case ComponentEnum.TOGGLE_TEXT:
      return { title: "", body: "" };
    case ComponentEnum.USER_LIST:
      return {
        label: "",
        placeholder_1: "",
        variable: "",
      };
    case ComponentEnum.CUSTOM_LINK:
      return {
        button_text: "",
        url: "",
        url_type: "external",
        icon_url: "",
        icon_alt: "",
        image_type: ImageTypeEnum.ICON,
      };
    case ComponentEnum.ACTION_DETAIL:
      return {
        linked_variables: [],
        detail_type: "",
        label: "",
      };

    case ComponentEnum.PRIORITY_LIST:
      return {
        label: "",
        choice_1: "",
        choice_2: "",
        choice_3: "",
        linkedVariable: [],
        variable: "",
      };
    case ComponentEnum.DATE_TIME:
      return {
        label: "",
        type: DateTimeTypeEnum.DATE,
        variable: "",
      };
    case ComponentEnum.CALLOUT_BOX:
      return {
        word: "",
        definition: "",
        image_src: "",
        image_alt: "",
        image_type: ImageTypeEnum.ICON,
        image_width: 100,
        image_hasBorder: false,
      };
    case ComponentEnum.FOR_EACH:
      return {
        parsingVariable: "",
        componentsList: [],
      };
    case ComponentEnum.TOGGLE:
      return {
        title: "",
        componentsList: [],
      };
    case ComponentEnum.ADD_TO_CALENDAR:
      return {
        description: "",
        eventTitle: "",
        recurrence: "NONE",
        startTime: DEFAULT_START_TIME,
        endTime: DEFAULT_END_TIME,
        addBacklink: true,
      };
    default: {
      const _exhaustiveCheck: never = componentType;
      throw new Error(`Unhandled component type: ${_exhaustiveCheck}`);
    }
  }
};
